import type { Permission } from '@arquivo-morto/contracts';
import {
  type ModuleDef,
  type PageDef,
  pagePath,
  type RouteDef,
} from '#src/app/navigation/module';

export type CanAccess = (permission: Permission | undefined) => boolean;

export interface VisibleModule {
  readonly module: ModuleDef;
  readonly pages: readonly PageDef[];
}

/** Módulos com as páginas que a pessoa pode abrir. Módulo sem página some. */
export function visibleModules(
  modules: readonly ModuleDef[],
  can: CanAccess,
): VisibleModule[] {
  return modules
    .map((module) => ({
      module,
      pages: module.pages.filter((page) => can(page.permission)),
    }))
    .filter(({ pages }) => pages.length > 0);
}

export interface RouteMatch {
  readonly module: ModuleDef;
  /** A página que contém o endereço (o detalhe de uma caixa está em "Caixas"). */
  readonly page: PageDef | undefined;
  /** Rota interna, quando o endereço é uma delas. */
  readonly route: RouteDef | undefined;
}

/** `caixas/:id` casa com `caixas/123`: cada parâmetro vale um segmento. */
function matchesPattern(pattern: string, path: string): boolean {
  const expected = pattern.split('/');
  const actual = path.split('/');
  return (
    expected.length === actual.length &&
    expected.every(
      (segment, index) => segment.startsWith(':') || segment === actual[index],
    )
  );
}

/** Módulo e página de um endereço, para o breadcrumb e o item selecionado. */
export function findRoute(
  modules: readonly ModuleDef[],
  pathname: string,
): RouteMatch | undefined {
  const module = modules.find(
    (candidate) =>
      pathname === `/${candidate.path}` ||
      pathname.startsWith(`/${candidate.path}/`),
  );
  if (module === undefined) {
    return undefined;
  }
  const page = module.pages.find((candidate) => {
    const path = pagePath(module, candidate);
    return pathname === path || pathname.startsWith(`${path}/`);
  });
  const relative = pathname.slice(module.path.length + 2);
  const route = module.routes?.find((candidate) =>
    matchesPattern(candidate.path, relative),
  );
  return { module, page, route };
}

/** Uma tela que exige a permissão: o caminho e como a navegação a nomeia. */
export interface Screen {
  readonly path: string;
  readonly label: string;
}

/**
 * As telas (páginas e rotas internas) que exigem a permissão para abrir.
 * Botões dentro de uma tela não entram: a descrição da permissão os cita.
 */
export function screensRequiring(
  modules: readonly ModuleDef[],
  permission: Permission,
): Screen[] {
  return modules.flatMap((module) => [
    ...module.pages
      .filter((page) => page.permission === permission)
      .map((page) => ({
        path: pagePath(module, page),
        label: `${module.title} > ${page.title}`,
      })),
    ...(module.routes ?? [])
      .filter((route) => route.permission === permission)
      .map((route) => ({
        path: `/${module.path}/${route.path}`,
        label: `${module.title} > ${route.title}`,
      })),
  ]);
}
